import type { AdminTextbookRequestView } from '@/api/textbookRequests'
import { textbookRequestBookLabel } from './textbookRequestStatus'

export interface TextbookRequestCenterGroup {
  organizationId: number
  organizationName: string | null
  organizationSubdomain: string | null
  requests: AdminTextbookRequestView[]
}

export interface HappydayItemSummary {
  key: string
  label: string
  quantity: number
}

export function groupTextbookRequestsByCenter(
  requests: AdminTextbookRequestView[],
): TextbookRequestCenterGroup[] {
  const groupsByOrganization = new Map<number, TextbookRequestCenterGroup>()

  for (const request of requests) {
    const group = groupsByOrganization.get(request.organizationId)
    if (group) {
      group.requests.push(request)
      continue
    }
    groupsByOrganization.set(request.organizationId, {
      organizationId: request.organizationId,
      organizationName: request.organizationName,
      organizationSubdomain: request.organizationSubdomain,
      requests: [request],
    })
  }

  return Array.from(groupsByOrganization.values())
}

export function filterTextbookRequestGroups(
  groups: TextbookRequestCenterGroup[],
  search: string,
) {
  const keyword = search.trim().toLowerCase()
  if (!keyword) return groups

  return groups.filter((group) => {
    const haystack = [
      group.organizationName,
      group.organizationSubdomain,
      ...group.requests.flatMap((request) => [
        request.learnerName,
        request.learnerEmail,
        request.bookTitle,
        request.volumeLabel,
        request.triggerLessonTitle,
      ]),
    ].filter(Boolean).join(' ').toLowerCase()
    return haystack.includes(keyword)
  })
}

export function summarizeHappydayItems(
  requests: AdminTextbookRequestView[],
): HappydayItemSummary[] {
  const summaries = new Map<string, HappydayItemSummary>()

  for (const request of requests) {
    const key = request.happydayItemCode != null
      ? `item:${request.happydayItemCode}`
      : request.happydayIsbnCode
        ? `isbn:${request.happydayIsbnCode}`
        : `missing:${request.id}`
    const label = request.happydayItemCode != null
      ? `상품코드 ${request.happydayItemCode}`
      : request.happydayIsbnCode
        ? `ISBN ${request.happydayIsbnCode}`
        : `매핑 없음 · ${request.bookTitle ?? `#${request.printBookId}`}`
    const existing = summaries.get(key)
    if (existing) {
      existing.quantity += 1
    } else {
      summaries.set(key, { key, label, quantity: 1 })
    }
  }

  return Array.from(summaries.values())
}

export interface TextbookRequestBookGroup {
  printBookId: number
  label: string
  mapped: boolean
  requests: AdminTextbookRequestView[]
}

export function hasHappydayProductMapping(request: AdminTextbookRequestView) {
  return request.happydayItemCode != null || !!request.happydayIsbnCode
}

/**
 * 센터별 출고 다이얼로그의 선택 목록용. 교재(권) 단위로 묶고,
 * 교재는 라벨순, 학생은 이름순으로 정렬한다.
 */
export function groupTextbookRequestsByBook(
  requests: AdminTextbookRequestView[],
): TextbookRequestBookGroup[] {
  const groupsByBook = new Map<number, TextbookRequestBookGroup>()

  for (const request of requests) {
    const group = groupsByBook.get(request.printBookId)
    if (group) {
      group.requests.push(request)
      continue
    }
    groupsByBook.set(request.printBookId, {
      printBookId: request.printBookId,
      label: textbookRequestBookLabel(request),
      mapped: hasHappydayProductMapping(request),
      requests: [request],
    })
  }

  const collator = new Intl.Collator('ko')
  return Array.from(groupsByBook.values())
    .map((group) => ({
      ...group,
      requests: [...group.requests].sort((a, b) =>
        collator.compare(a.learnerName ?? '', b.learnerName ?? '') || a.id - b.id),
    }))
    .sort((a, b) => collator.compare(a.label, b.label) || a.printBookId - b.printBookId)
}

/** 기본 선택: 해피데이 상품 매핑이 있는 요청만. 매핑 없는 요청은 출고에서 자동 제외된다. */
export function defaultDispatchSelection(requests: AdminTextbookRequestView[]): Set<number> {
  return new Set(requests.filter(hasHappydayProductMapping).map((request) => request.id))
}
